import { IPaginator, PatientKind, SubscriptionFrequency } from "common/types";
import { parseNumber } from "common/utils/numbers";
import {
  convertStringPassthrough,
  pluralized,
  splitStringToValidValues,
} from "common/utils/strings";
import { Optional } from "common/utils/types";
import { isValueSet } from "common/utils";
import { toDate } from "common/utils/dates";

import { Paths } from "client/routes";

import {
  CarouselDisplayCarousel,
  CategoryCarousel,
  SelectedProductFilters,
  SelectedCategoryProductLineFilters,
  IProductItem,
  SubscriptionSelectItemProps,
  SelectedPromotionProductFilters,
  PriceBreak,
} from "../types";

// `category === null` is the canonical Top Sellers carousel; remaining
// carousels render as category sections in the order returned by the BE.
export const partitionCarousels = (carousels: CarouselDisplayCarousel[]) => {
  const topSellerCarousel = carousels.find((c) => c.category === null);
  const categoryCarousels = carousels.filter((c): c is CategoryCarousel => c.category !== null);
  return { topSellerCarousel, categoryCarousels };
};

const parsedIds = (ids: Optional<number[] | string>) => {
  if (!ids) {
    return [];
  }
  if (ids instanceof Array) {
    return ids;
  }
  return (
    ids
      .split(",")
      .map((val) => parseNumber(val, 0))
      .filter((val) => !!val) ?? []
  );
};

const parsedBoolean = (bool: Optional<boolean[] | string>) => {
  if (!isValueSet(bool)) {
    return [];
  }
  if (bool instanceof Array) {
    return bool;
  }
  return bool.split(",").map((val) => val === "true");
};

export const getProductFiltersFromSearch = ({
  category_ids,
  manufacturer_ids,
  species,
  incentive_ids,
  rebate_ids,
  prescription_required,
}: Paths["products"]["search"]["Search"]): SelectedProductFilters => {
  const validPatientKinds = new Set(Object.values(PatientKind));
  return {
    category_ids: parsedIds(category_ids),
    manufacturer_ids: parsedIds(manufacturer_ids),
    species: convertStringPassthrough<PatientKind[]>(species ?? "", (kinds) =>
      splitStringToValidValues<PatientKind>(kinds, validPatientKinds),
    ),
    incentive_ids: parsedIds(incentive_ids),
    rebate_ids: parsedIds(rebate_ids),
    prescription_required: parsedBoolean(prescription_required),
  };
};

export const getRebateProductFiltersFromSearch = ({
  category_ids,
  manufacturer_ids,
  species,
  prescription_required,
}: Paths["products"]["rebate"]["Search"]): SelectedPromotionProductFilters => {
  const validPatientKinds = new Set(Object.values(PatientKind));
  return {
    category_ids: parsedIds(category_ids),
    manufacturer_ids: parsedIds(manufacturer_ids),
    species: convertStringPassthrough<PatientKind[]>(species ?? "", (kinds) =>
      splitStringToValidValues<PatientKind>(kinds, validPatientKinds),
    ),
    prescription_required: parsedBoolean(prescription_required),
  };
};

export const getIncentiveProductFiltersFromSearch = ({
  category_ids,
  manufacturer_ids,
  species,
  prescription_required,
}: Paths["products"]["incentive"]["Search"]): SelectedPromotionProductFilters => {
  const validPatientKinds = new Set(Object.values(PatientKind));
  return {
    category_ids: parsedIds(category_ids),
    manufacturer_ids: parsedIds(manufacturer_ids),
    species: convertStringPassthrough<PatientKind[]>(species ?? "", (kinds) =>
      splitStringToValidValues<PatientKind>(kinds, validPatientKinds),
    ),
    prescription_required: parsedBoolean(prescription_required),
  };
};

export const getCategoryProductLineFiltersFromSearch = ({
  manufacturer_ids,
  species,
  prescription_required,
}: Paths["products"]["category"]["Search"]): SelectedCategoryProductLineFilters => {
  const validPatientKinds = new Set(Object.values(PatientKind));
  return {
    manufacturer_ids: parsedIds(manufacturer_ids),
    species: convertStringPassthrough<PatientKind[]>(species ?? "", (kinds) =>
      splitStringToValidValues<PatientKind>(kinds, validPatientKinds),
    ),
    prescription_required: parsedBoolean(prescription_required),
  };
};

export const getSearchString = (paginator?: IPaginator, entityDescription: string = "result") => {
  const start = paginator?.page_start_index || 0;
  const end = paginator?.page_end_index || 0;
  const count = paginator?.count || 0;
  const entity = pluralized(count, entityDescription);
  return `Showing ${start} - ${end} of ${count} ${entity}`;
};

/* Used for items with price break data */
export const getItemPriceFromQuantity = (
  item: IProductItem | SubscriptionSelectItemProps,
  quantity: number,
  subscription: SubscriptionFrequency | null,
  priceBreaks: PriceBreak[],
) => {
  if (priceBreaks.length <= 1) {
    return subscription
      ? item.subscription_discounted_price
      : item.non_subscription_discounted_price;
  }
  const relevantPriceBreak = priceBreaks.find(
    (priceBreak) => quantity >= priceBreak.low_qty && quantity <= priceBreak.high_qty,
  );
  if (relevantPriceBreak) {
    return relevantPriceBreak.price;
  }
  return subscription ? item.subscription_discounted_price : item.non_subscription_discounted_price;
};

export const toPromotionDateRange = (
  start: string | null = null,
  end: string | null = null,
): string => {
  if (start && end) {
    return `Offer valid from ${toDate(start)} to ${toDate(end)}`;
  }
  if (!start && end) {
    return `Offer valid until ${toDate(end)}`;
  }
  if (start && !end) {
    return `Offer valid since ${toDate(start)}`;
  }
  return "Offer always valid. No expiration";
};

type PromoCodePunctuation = {
  [key: string]: string;
};

/**
 * Determines the proper punctuation and "or"s to be added between promo codes.
 *
 * @param codes The promo codes that the user can choose from
 * @returns A dictionary where key is promo code and value is string to prepend
 */
export const getPromoCodePunctuation = (codes: string[]) => {
  const numCodes = codes.length;
  const stringsToPrepend: PromoCodePunctuation = {};

  codes.forEach((code, index) => {
    if (index === 0) {
      stringsToPrepend[code] = "";
    } else if (index === numCodes - 1) {
      stringsToPrepend[code] = numCodes === 2 ? " or " : ", or ";
    } else {
      stringsToPrepend[code] = ", ";
    }
  });
  return stringsToPrepend;
};

/**
 * Calculate dose price from unit price and units count.
 * Returns null if units_count is not available or zero, or if unitPrice is not a valid number.
 */
export const calculateDosePrice = (unitPrice: number, unitsCount: number | null): number | null => {
  if (!unitsCount || unitsCount <= 0 || !Number.isFinite(unitPrice)) {
    return null;
  }
  return Math.round(unitPrice / unitsCount);
};

/**
 * Check if dose pricing is available for an item.
 * Validates units_count and dose_label to ensure complete dose pricing display.
 */
export const hasDosePricing = (item: {
  units_count: number | null;
  dose_label: string | null;
}): boolean => {
  return item.units_count !== null && item.units_count > 0 && item.dose_label !== null;
};
